import React, { Suspense, useState } from 'react';
import { useSelector } from 'react-redux';
import cc from 'classcat';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { GLOBALS } from 'utils/generic/global-variables';
import { lazy } from 'utils/generic/lazy';
import { mountPoint } from 'utils/generic/route-manipulation';
import { ENotRealSports } from 'interfaces/not-real-sports';
import { ESportsbookLayoutTypes } from 'interfaces/sportsbook-configs';
import { Game } from 'interfaces/sportsbook-data-levels';
import { ExpandedMarkets } from 'newelements/Sportsbook/layouts/advanced/Desktop/__partials/AdvancedMatchCard/ExpandedMarkets';
import { TMatchCardInlineProps } from 'components/MatchCardInline';
import { MatchCardInlineSkeleton } from 'components/MatchCardInline/Skeleton';
import { saveCurrentLocation } from 'components/QueryRoute';
import { MatchCardInlineUSASkeleton } from 'components/Shared/MatchCardInlineUSA/__partials/Skeleton';
import { ORIGINAL_MARKET_COUNT } from 'newcomponents/Shared/sportsbook/SportsbookGameList';
import { useSportsbookConfigs } from 'hooks/sportsbook/useSportsbookConfigs';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';
import { getMarketsCount } from 'store/selectors/new-sportsbook';
import './index.less';

const { MatchCardInlineUSA } = lazy(
  () =>
    import(
      /* webpackChunkName: "events-item-view-usa" */ 'components/Shared/MatchCardInlineUSA'
    )
);

const { MatchCardInline } = lazy(
  () =>
    import(
      /* webpackChunkName: "match-card-in-line" */ 'components/MatchCardInline'
    )
);

type TAdvancedMatchCardInLineWrapperProps = {
  handleMatchCardClick: ({
    sportAlias,
    regionAlias,
    competitionId,
    gameId
  }: {
    sportAlias: string;
    regionAlias: string;
    competitionId: number;
    gameId: number;
  }) => void;
};

export const AdvancedMatchCardInLineWrapper = ({
  game,
  handleMatchCardClick,
  isLive
}: Omit<TMatchCardInlineProps, 'onClick'> &
  TAdvancedMatchCardInLineWrapperProps) => {
  const [showExpandedMarkets, setShowExpandedMarkets] = useState(false);

  const configs = useSportsbookConfigs();
  const { spbLayoutType: layoutType } = configs;
  const { params } = useSbRouteMatch();
  const getterParam = `${!isLive ? params.competition : params.sport}_${
    configs.pageType
  }`;

  const marketsCount = useSelector(getMarketsCount)[getterParam] || null;

  const { isExact } = useSbRouteMatch(
    `${mountPoint(location.pathname)}/team/:sport/:teamId?`
  );

  const handleMatchCardClickWrapper = () => {
    if (isExact) {
      GLOBALS.sportsBook.redirectedFromFavorite = true;
    }

    if (window.getPathname().includes(`/${ENotRealSports.team}`)) {
      saveCurrentLocation('mobileSportsbookGames');
    }

    handleMatchCardClick({
      sportAlias: game.sport_alias || '',
      regionAlias: game.regionAlias || '',
      competitionId: game.competitionId || 0,
      gameId: game.id
    });
  };

  const originalMarketsCount = SpringConfigs.MOCKED_DATA
    ? ORIGINAL_MARKET_COUNT
    : marketsCount?.[game.id];

  return (
    <div
      className={cc([
        'advancedMatchCardInLineWrapper',
        {
          'advancedMatchCardInLineWrapper--expanded': showExpandedMarkets
        }
      ])}
    >
      {layoutType === ESportsbookLayoutTypes.AMERICAN ? (
        <Suspense fallback={<MatchCardInlineUSASkeleton />}>
          <MatchCardInlineUSA
            game={{
              ...game,
              markets_count: originalMarketsCount
            }}
            competitionName={game.competitionName}
            sportAlias={game.sport_alias}
            comp={{ id: game.competitionId }}
            externalRedirect={handleMatchCardClickWrapper}
            withMoreWagesWrapper={true}
            regionAlias={game.regionAlias}
            showBBIcon
            showFavoriteButton
            isLive={isLive}
            detailedTeamsInfo
          />
        </Suspense>
      ) : (
        <Suspense fallback={<MatchCardInlineSkeleton />}>
          <MatchCardInline
            key={game.id}
            longDate
            game={game as Game}
            onClick={handleMatchCardClickWrapper}
            currentSport={game.sport_alias || ''}
            competitionName={game.competitionName || ''}
            isLive={isLive}
            showExpandedMarkets={showExpandedMarkets}
            setShowExpandedMarkets={setShowExpandedMarkets}
          />
        </Suspense>
      )}

      {showExpandedMarkets && game && (
        <ExpandedMarkets
          game={game}
          isLive={!!isLive}
          handleShowMoreClick={handleMatchCardClickWrapper}
        />
      )}
    </div>
  );
};
